import { AnimatePresence, motion, useDragControls, type PanInfo } from "framer-motion";
import { X } from "lucide-react";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/cn";
import { useLayoutMode } from "../../lib/useViewportSize";

interface Props {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Preferred slide-in side on desktop. Portrait phones always use a bottom
   *  sheet (thumbs are near the bottom); landscape phones always use a
   *  right-side sheet (a bottom sheet would cover the short screen). */
  side?: "right" | "bottom";
  children: React.ReactNode;
  width?: string;
}

/**
 * iOS-style sheet. Bottom on portrait phones (with a visible drag handle
 * and a swipe-down-to-dismiss gesture that targets the handle so scrollable
 * content underneath still pans natively). Right-side on landscape phones,
 * and on desktop when the caller asks for it.
 *
 * Heights use `dvh` (dynamic viewport height) so the sheet doesn't get
 * clipped by iOS Safari's collapsing URL bar. Safe-area insets pad both
 * the title (top sheet only) and the scrollable body so content never
 * sits underneath the home indicator.
 */
export function Sheet({ open, onClose, title, side = "bottom", children, width }: Props) {
  const layout = useLayoutMode();
  // Portrait phones get a bottom sheet no matter what the caller asked for.
  // Landscape phones are too short for one, so they get a right-side sheet
  // (clear of the mode rail); desktop honors the caller.
  const effectiveSide: "right" | "bottom" =
    layout === "phoneLandscape" ? "right" : layout === "phone" ? "bottom" : side;
  const sideWidth = width ?? (layout === "phoneLandscape" ? "min(420px, 62vw)" : "min(420px, 92vw)");
  const dragControls = useDragControls();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Lock background scroll/pull-to-refresh while the sheet is open. Without
  // this, iOS treats the page underneath as scrollable when the user pans
  // outside the sheet.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (typeof document === "undefined") return null;

  /** Dismiss if the user swiped down far enough or hard enough. The
   *  thresholds match Apple's UISheetPresentationController feel: a brisk
   *  flick (>500 px/s) or a noticeable drag (>120 px) closes the sheet. */
  function onHandleDragEnd(_e: PointerEvent | MouseEvent | TouchEvent, info: PanInfo) {
    if (info.velocity.y > 500 || info.offset.y > 120) onClose();
  }

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-ink-900/30 backdrop-blur-[2px]"
          />
          <motion.div
            initial={
              effectiveSide === "bottom" ? { y: "100%" } : { x: "100%" }
            }
            animate={effectiveSide === "bottom" ? { y: 0 } : { x: 0 }}
            exit={effectiveSide === "bottom" ? { y: "100%" } : { x: "100%" }}
            transition={{ type: "spring", damping: 32, stiffness: 320 }}
            // Drag is only enabled for the bottom-sheet variant; the
            // drag handle starts it imperatively so scrollable body
            // content keeps its native pan-y behavior.
            drag={effectiveSide === "bottom" ? "y" : false}
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.4 }}
            onDragEnd={effectiveSide === "bottom" ? onHandleDragEnd : undefined}
            className={cn(
              "fixed z-50 bg-white shadow-2xl flex flex-col",
              effectiveSide === "bottom"
                ? // Cap at 92dvh so it never exceeds the visible viewport
                  // and leaves a strip of the canvas peeking through.
                  "left-0 right-0 bottom-0 rounded-t-3xl max-h-[92dvh]"
                : "top-0 right-0 bottom-0 rounded-l-3xl h-full pr-[env(safe-area-inset-right)] pt-[env(safe-area-inset-top)]"
            )}
            style={effectiveSide === "right" ? { width: sideWidth } : undefined}
          >
            {effectiveSide === "bottom" && (
              // Wide, tappable handle area. Pointer-down starts the
              // motion drag so the body's native scroll is preserved.
              <button
                type="button"
                aria-label="Drag to dismiss"
                onPointerDown={(e) => dragControls.start(e)}
                className="flex w-full justify-center pt-2 pb-1 -mb-1 touch-none cursor-grab active:cursor-grabbing select-none"
              >
                <div className="h-1.5 w-10 rounded-full bg-ink-300" />
              </button>
            )}
            <div className="flex items-center justify-between px-5 pt-2 pb-3">
              <h3 className="text-base font-semibold text-ink-900">{title}</h3>
              <button
                onClick={onClose}
                className="h-11 w-11 -mr-2 inline-flex items-center justify-center rounded-full hover:bg-ink-100 active:bg-ink-200 transition-colors"
                aria-label="Close"
              >
                <X className="size-5 text-ink-500" />
              </button>
            </div>
            <div
              className="flex-1 overflow-y-auto overscroll-contain px-5 pb-[calc(env(safe-area-inset-bottom)+1.5rem)]"
            >
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
}
